import {
  Autocomplete,
  CircularProgress,
  InputAdornment,
  ListItem,
  ListItemIcon,
  ListItemText,
  SxProps,
  TextField,
  TextFieldProps,
  Theme,
  Typography,
} from "@mui/material";
import { chain, isEmpty } from "lodash-es";
import { FocusEventHandler, ReactNode, useEffect, useMemo, useState } from "react";

import { TextHelper } from "@/common/helpers/text";
import { ApiEnumName, ApiEnumValue, enumService } from "@/common/services/enum";
import { AutocompleteOptionType, BaseAutocompleteOption } from "@/common/ts/autocomplete";

import AppIcon from "../Icons/AppIcon";

export type ApiEnumAutocompleteOption = BaseAutocompleteOption<ApiEnumValue<ApiEnumName>>;

const apiEnumToOption = <TEnumName extends ApiEnumName>(
  type: TEnumName,
  enumValue: ApiEnumValue<TEnumName>,
): ApiEnumAutocompleteOption => ({
  id: enumValue,
  title: enumService.getEnumValueName(type, enumValue),
  optionType: AutocompleteOptionType.Normal,
  data: enumValue,
});

export interface ApiEnumAutocompleteProps<TEnumName extends ApiEnumName> {
  type: TEnumName;
  value: ApiEnumValue<TEnumName> | null | undefined;
  allowNone?: boolean;
  /** Auto-select option if it's the single option in the list and the input is required. */
  isAutoSelectSingleOption?: boolean;
  disabledEnumValues?: Array<ApiEnumValue<TEnumName>> | null;
  onlyEnumValues?: Array<ApiEnumValue<TEnumName>> | null;
  excludeEnumValues?: Array<ApiEnumValue<TEnumName>> | null;
  onChange?: (newValue?: ApiEnumValue<TEnumName> | null) => void;

  // autocomplete specific props
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  size?: "small" | "medium";
  label?: ReactNode;
  textFieldProps?: TextFieldProps;
  sx?: SxProps<Theme>;
  onBlur?: FocusEventHandler;
}

/** Autocomplete for enums generated from Open API spec of WebApi. */
export default function ApiEnumAutocomplete<TEnumName extends ApiEnumName>({
  type,
  value,
  disabled,
  required,
  fullWidth,
  size,
  label,
  textFieldProps,
  allowNone = false,
  isAutoSelectSingleOption = true,
  disabledEnumValues,
  onlyEnumValues,
  excludeEnumValues,
  sx,
  onBlur,
  onChange,
}: ApiEnumAutocompleteProps<TEnumName>) {
  const [open, setOpen] = useState(false);
  const [isLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState<ApiEnumAutocompleteOption | null>(null);
  const [inputValue, setInputValue] = useState("");

  const keyValuePairs = useMemo(() => enumService.getEnumObjKeyValuePairs(type), [type]);
  const onlyEnumValuesMap = useMemo(
    () =>
      onlyEnumValues && !isEmpty(onlyEnumValues)
        ? chain(onlyEnumValues)
            .keyBy((x) => x)
            .mapValues((x) => true)
            .value()
        : undefined,
    [onlyEnumValues],
  );
  const excludeEnumValuesMap = useMemo(
    () =>
      excludeEnumValues && !isEmpty(excludeEnumValues)
        ? chain(excludeEnumValues)
            .keyBy((x) => x)
            .mapValues((x) => true)
            .value()
        : undefined,
    [excludeEnumValues],
  );

  const options = useMemo(
    () =>
      keyValuePairs
        .filter((kvp) => (allowNone ? true : kvp.value !== "None"))
        .filter((kvp) =>
          onlyEnumValuesMap ? onlyEnumValuesMap[kvp.key.toString()] === true : true,
        )
        .filter((kvp) => (excludeEnumValuesMap ? !excludeEnumValuesMap[kvp.key.toString()] : true))
        .map((kvp) => ({ value: kvp.value }))
        .map((x) => apiEnumToOption(type, x.value)),
    [keyValuePairs, onlyEnumValuesMap, excludeEnumValuesMap],
  );

  useEffect(() => {
    if (!value) {
      setSelectedOption(null);
    } else if (value && selectedOption?.data !== value) {
      setSelectedOption(apiEnumToOption<typeof type>(type, value));
    }
  }, [value, selectedOption, options]);

  // handle isAutoSelectSingleOption
  useEffect(() => {
    const singleOption = options.length === 1 ? options[0] : undefined;
    if (
      isAutoSelectSingleOption &&
      required &&
      !isLoading &&
      !value &&
      !selectedOption &&
      singleOption
    ) {
      setSelectedOption(singleOption);
      onChange && onChange(singleOption.data as ApiEnumValue<TEnumName> | null | undefined);
    }
  }, [isAutoSelectSingleOption, required, isLoading, value, selectedOption, options]);

  return (
    <Autocomplete
      sx={{ minWidth: 200, ...sx }}
      disabled={disabled}
      fullWidth={fullWidth}
      size={size}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      isOptionEqualToValue={(option, value2) => option.id === value2.id}
      getOptionLabel={(option) => option.title.toString()}
      getOptionDisabled={
        (disabledEnumValues &&
          ((option) =>
            (option.data && disabledEnumValues.includes(option.data as ApiEnumValue<TEnumName>)) ||
            false)) ||
        undefined
      }
      options={options}
      loading={isLoading}
      autoComplete
      includeInputInList
      filterSelectedOptions={false}
      value={selectedOption}
      inputValue={inputValue}
      onBlur={onBlur}
      onChange={(event, newValue) => {
        setSelectedOption(newValue);
        onChange && onChange(newValue?.data as ApiEnumValue<TEnumName> | null | undefined);
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <TextField
          {...textFieldProps}
          {...params}
          label={label || textFieldProps?.label || type}
          placeholder='Search...'
          fullWidth
          required={required}
          value={inputValue}
          // slotProps={{
          //   input: {},
          // }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <InputAdornment position='end'>
                {isLoading ? <CircularProgress color='inherit' size={20} /> : null}
                {params.InputProps.endAdornment}
                {textFieldProps?.InputProps?.endAdornment}
              </InputAdornment>
            ),
          }}
        />
      )}
      renderOption={(props, option) => {
        return (
          <ListItem {...props} key={option.id}>
            <ListItemIcon>
              <AppIcon of='data' />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography component='div' variant='body1'>
                  {TextHelper.humanize(option.title)}
                </Typography>
              }
              secondary={
                <>
                  {TextHelper.humanize(
                    enumService.getEnumValueDescription(
                      type,
                      option.data as ApiEnumValue<TEnumName>,
                    ),
                  )}
                </>
              }
            />
          </ListItem>
        );
      }}
    />
  );
}
